import { PrimaryButton, SecondaryButton } from '../Button';
import { InputField } from '../Form';
import styled from 'styled-components';
import { IAliasOption } from './types';
import { useEffect, useRef, useState } from 'react';
import { assertCondition } from '../../utils/types';

const MAX_ALIAS_LENGTH = 50;

interface IProps {
  aliasOption: IAliasOption;
  dismissMenu: () => void;
}

const StyledInput = styled(InputField)`
  width: 216px;
  height: 32px;
`;
const ActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 12px;
`;

const Container = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
`;

const RenameMenu = ({ aliasOption, dismissMenu }: IProps) => {
  const ref = useRef<HTMLInputElement>(null);
  assertCondition(aliasOption.type === 'alias');

  const originName = aliasOption.alias ?? aliasOption.name;
  const [alias, setAlias] = useState(originName);

  const onReset = () => {
    aliasOption.reset();
    dismissMenu();
  };

  const onSave = () => {
    aliasOption.apply(alias);
    dismissMenu();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (alias.length > 0 && event.code === 'Enter') {
      // prevent the behavior of enter the parent menu item..
      // which will re-open the current menu again.
      event.preventDefault();
      aliasOption.apply(alias);
      dismissMenu();
    }
  };

  /**
   * Focus the input field after the menu is rendered.
   * The default autoFocus in <InputField /> will be override in the behavior of <ContextualMenu />
   * This is a workaround to ensure the input field is focused after the menu is rendered.
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      ref.current?.focus();
    }, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <Container>
      <ContentRow>
        <StyledInput
          ref={ref}
          value={alias}
          placeholder={originName}
          onChange={setAlias}
          maxLength={MAX_ALIAS_LENGTH}
          onKeyDown={handleKeyDown}
        />
      </ContentRow>
      <ActionsContainer>
        <SecondaryButton
          type="button"
          onClick={onReset}
          size="regular"
          trackContext="aliases"
          trackAction="reset"
        >
          Reset
        </SecondaryButton>
        <PrimaryButton
          type="button"
          disabled={alias.length === 0}
          onClick={onSave}
          size="regular"
          trackContext="aliases"
          trackAction="confirm"
        >
          Confirm
        </PrimaryButton>
      </ActionsContainer>
    </Container>
  );
};

export default RenameMenu;
